import type { ReactNode } from "react";
import type { Column } from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowDownIcon,
  ArrowLeftToLineIcon,
  ArrowRightToLineIcon,
  ArrowUp,
  ArrowUpIcon,
  ChevronsUpDown,
  EyeOffIcon,
  MoreVerticalIcon,
  PinOffIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { cn } from "@/lib/utils";

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  children?: ReactNode;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  children,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const handleVisibilityToggle = () => {
    column.toggleVisibility(false);
  };

  const handlePinLeft = () => {
    column.pin("left");
  };

  const handlePinRight = () => {
    column.pin("right");
  };

  const handleUnpin = () => {
    column.pin(false);
  };

  const handleSortAsc = () => {
    column.toggleSorting(false);
  };

  const handleSortDesc = () => {
    column.toggleSorting(true);
  };

  return (
    <div className={cn("group flex items-center px-3 py-[6px]")}>
      {!column.getCanSort() ? (
        (children ?? <div className={cn("font-semibold")}>{title}</div>)
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 h-8"
          onClick={() => {
            column.toggleSorting(!column.getIsSorted() ? true : column.getIsSorted() === "asc");
          }}
        >
          <span>{title}</span>
          {column.getIsSorted() === "desc" ? (
            <ArrowDown />
          ) : column.getIsSorted() === "asc" ? (
            <ArrowUp />
          ) : (
            <ChevronsUpDown className="text-muted-foreground/50" />
          )}
        </Button>
      )}

      <span className="flex-1" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="invisible size-8 group-hover:visible data-[state=open]:visible"
          >
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48" align="start">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={handleSortAsc} disabled={!column.getCanSort()}>
              <ArrowUpIcon />
              Sort Ascending
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleSortDesc} disabled={!column.getCanSort()}>
              <ArrowDownIcon />
              Sort Descending
            </DropdownMenuItem>
            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={handlePinLeft} disabled={column.getIsPinned() === "left"}>
              <ArrowLeftToLineIcon />
              Pin Left
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handlePinRight} disabled={column.getIsPinned() === "right"}>
              <ArrowRightToLineIcon />
              Pin Right
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleUnpin} disabled={!column.getIsPinned()}>
              <PinOffIcon />
              Unpin
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleVisibilityToggle}>
              <EyeOffIcon />
              Hide
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
